import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { StationeryPriceInfoRawLineItem, StationeryProduct } from '@shared/utils/print-price/types';
import { useCallback } from 'react';
import type { PriceSummaryLineItem } from '../PriceSummary.types';
import { PriceSummaryCurrencyFormatOptions } from '../PriceSummary.constants';
import { useProductAttributes } from '@apps/card/components/ProductUIConfig/useProductAttributes';
import { useShippingMethodLabelsAndDescriptions } from '@apps/card/hooks/useShippingMethodLabelsAndDescriptions';
import { addError } from '@shared/utils/logger';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useTranslation } from '@shared/core';

// Converts fancy line items to translated Label / Value pairs
export const usePriceCalculatorLineItemsToPriceSummaryLineItems = (product: StationeryProduct) => {
  const attributes = useProductAttributes();
  const { tq } = useTranslation('stationery');
  const formatPrice = useMinorUnitsToPriceString();

  const { orderCurrencyISO4217: orderCurrency } = useStationeryProductPricingContext(false);

  const shippingMethodLabelsAndDescriptions = useShippingMethodLabelsAndDescriptions();

  const toLineItems = useCallback(
    (lineItems: StationeryPriceInfoRawLineItem[], stepFilter?: string[], includeQuantity?: boolean): PriceSummaryLineItem[] => {
      const currencyOptions = orderCurrency ? { ...PriceSummaryCurrencyFormatOptions, currency: orderCurrency } : PriceSummaryCurrencyFormatOptions;
      return lineItems
        .map(item => {
          switch (item.type) {
            case 'base_cost': {
              // Replace 'cards' with some string on ProductConfig.
              // for cases like 'napkins,' 'signs,' etc...
              if (stepFilter && !includeQuantity) {
                return null;
              }

              const unitPriceFormatted = formatPrice(item.unitPriceInMinorUnits, currencyOptions);
              const totalPriceFormatted = formatPrice(item.totalPriceInMinorUnits, currencyOptions);
              return [tq`Quantity: ${item.quantity} items at ${unitPriceFormatted} each`, totalPriceFormatted];
            }
            case 'option': {
              const attribute = attributes.find(attr => attr.attributeId === item.attributeId);
              if (!attribute) return null;

              const option = attribute.options.find(opt => opt.optionId === item.optionId);
              const attributeName = attribute?.lineItem ?? '';
              const optionName = option?.lineItem ?? '';

              if (stepFilter && attribute?.step && !stepFilter.includes(attribute.step)) {
                return null;
              }

              const totalPriceFormatted = formatPrice(item.totalPriceInMinorUnits, currencyOptions);
              return [`${attributeName}${optionName ? `: ${optionName}` : ''}`, totalPriceFormatted];
            }
            case 'delivery': {
              if (stepFilter && !stepFilter.includes('shipping')) {
                return null;
              }

              const deliveryId = item.deliveryMethodId;
              // item.totalPriceInMinorUnits is already converted to the target currency by PrintPriceCalculator
              const deliveryPriceFormatted = formatPrice(item.totalPriceInMinorUnits, currencyOptions);

              const deliveryLabel = shippingMethodLabelsAndDescriptions[deliveryId as keyof typeof shippingMethodLabelsAndDescriptions].title;

              if (!deliveryLabel) {
                addError('Invalid delivery label for line item.', { deliveryId, productId: product.id });
              }

              return [tq`Shipping: ${deliveryLabel}`, deliveryPriceFormatted, { highlight: item.totalPriceInMinorUnits === 0 }];
            }
            case 'tax': {
              if (stepFilter && !stepFilter.includes('salesTax')) {
                return null;
              }

              const taxFormatted = formatPrice(item.totalPriceInMinorUnits, currencyOptions);
              return [tq`Sales Tax`, taxFormatted];
            }
          }
        })
        .filter(lineItem => lineItem !== null) as PriceSummaryLineItem[];
    },
    [attributes, formatPrice, product.id, shippingMethodLabelsAndDescriptions, orderCurrency, tq],
  );

  return toLineItems;
};
